/**
 * @file Secondary Nav.
 */

import { gsap } from 'gsap';

/**
 * Secondary Nav component.
 */
const vSecondaryNav = () => {
	const secondaryNav = document.querySelector('.v-secondary-nav');

	if (!secondaryNav) {
		return;
	}

	const trigger = secondaryNav.querySelector('.v-secondary-nav__trigger');
	const inner = secondaryNav.querySelector('.v-secondary-nav__inner');

	gsap.set(inner, {
		height: 0,
		overflow: 'hidden',
	});

	gsap.set('.v-secondary-nav__title', {
		opacity: 0,
	});

	const menuAnimation = gsap
		.timeline({
			defaults: {
				ease: 'sine.out',
				duration: 0.2,
			},
			onReverseComplete: () => {
				gsap.set(inner, {
					display: 'none',
				});
			},
			paused: true,
		})
		.addLabel('start')
		.to('.v-secondary-nav__trigger-label', {
			opacity: 0,
		})
		.to(
			'.v-secondary-nav__title',
			{
				opacity: 1,
			},
			'>',
		)
		.fromTo(
			inner,
			{
				height: 0,
			},
			{
				id: 'wrap-height',
				height: 'auto',
				duration: 0.4,
			},
			'start',
		);

	menuAnimation.reversed(true);

	trigger.addEventListener('click', () => {
		if (menuAnimation.reversed()) {
			gsap.set(inner, {
				display: 'block',
			});

			const e = new CustomEvent('v-secondary-nav-open', {
				detail: {
					open: true,
				},
			});
			window.dispatchEvent(e);

			menuAnimation.play();
			trigger.classList.add('v-secondary-nav__trigger--open');
		} else {
			trigger.classList.remove('v-secondary-nav__trigger--open');
			menuAnimation.reverse();
		}
	});

	document.body.addEventListener('click', (e) => {
		if (
			e.target.classList.contains('v-secondary-nav__wrap') ||
			e.target.closest('.v-secondary-nav__wrap')
		) {
			return;
		}

		trigger.classList.remove('v-secondary-nav__trigger--open');
		menuAnimation.reverse();
	});

	const submenuClasses = [];

	class SubMenu {
		constructor(submenu, i) {
			this.id = i;
			this.wrap = submenu;
			this.trigger = submenu.querySelector(':scope > a');
			this.inner = submenu.querySelector(':scope > .children');
			this.isOpen = false;
			this.isAnimating = false;
			this.animationCompleteCb = null;
			this.animation = null;
			this.initialOpen = false;

			this.init();
		}

		openMenu(jump = false) {
			const openMenus = submenuClasses.filter((s) => s.isOpen);
			[...openMenus].forEach((m) => {
				m.closeMenu();
			});

			this.isOpen = true;
			this.isAnimating = true;
			gsap.set(this.inner, {
				display: 'block',
				height: 0,
				overflow: 'hidden',
			});

			menuAnimation
				.getChildren()
				.filter((a) => a.vars.id === 'wrap-height')[0]
				.invalidate();

			this.trigger.classList.add('v-secondary-nav__trigger--open');

			this.animationSetup();

			if (jump) {
				this.animation.progress(1);
			} else {
				this.animation.play();
			}
		}

		closeMenu() {
			this.isOpen = false;
			this.isAnimating = true;
			this.trigger.classList.remove('v-secondary-nav__trigger--open');
			menuAnimation
				.getChildren()
				.filter((a) => a.vars.id === 'wrap-height')[0]
				.invalidate();
			this.animation.reverse();
		}

		animationSetup() {
			if (!this.animation) {
				gsap.set(this.inner, {
					height: 0,
					overflow: 'hidden',
				});

				this.animation = gsap.to(this.inner, {
					onComplete: () => {
						this.isAnimating = false;
						if (typeof this.animationCompleteCb === 'function') {
							this.animationCompleteCb();
							this.animationCompleteCb = null;
						}
					},
					onReverseComplete: () => {
						this.isAnimating = false;
						gsap.set(this.inner, {
							clearProps: 'all',
						});
					},
					duration: 0.3,
					height: 'auto',
					ease: 'sine.out',
					paused: true,
				});

				if (this.initialOpen) {
					this.openMenu(true);
					this.initialOpen = false;
				}
			}
		}

		init() {
			if (
				this.wrap.classList.contains('current_page_item') ||
				this.wrap.classList.contains('current_page_parent') ||
				this.wrap.classList.contains('current_page_ancestor')
			) {
				this.initialOpen = true;
			}

			if (gsap.getProperty(trigger, 'display') === 'none') {
				this.animationSetup();
			}

			window.addEventListener('v-secondary-nav-open', (e) => {
				if (e.detail.open) {
					this.animationSetup();
				}
			});

			this.trigger.addEventListener('click', (e) => {
				e.preventDefault();
				if (this.isOpen) {
					this.closeMenu();
				} else {
					this.openMenu();
				}
			});
		}
	}

	const submenus = secondaryNav.querySelectorAll(
		'.v-secondary-nav__list > .page_item_has_children',
	);
	[...submenus].forEach((sub, i) => {
		submenuClasses.push(new SubMenu(sub, i));
	});

	secondaryNav.classList.remove('v-secondary-nav--no-init');
};

export default vSecondaryNav;
